// client/src/lib/debitNoteApi.ts
//
// Vendor return receipts (debit notes, DN-00001), D44 (2026-09-17). Typed over
// /api/debit-notes. Every figure a return stores is worked out by the server
// (services/debitNote.service.js): the form asks `previewDebitNote` for the
// amounts rather than multiplying anything itself, so the screen and the saved
// return cannot disagree. Refusals carry a machine-readable `code` and, for a
// line, `validationErrors: [{ lineNumber, code }]`.
import { API_BASE_URL, get, postFormData, postJson, put } from './apiClient';
import { getAuthToken } from './authSession';

export type DebitNoteKind = 'goods' | 'money_only';
export type DebitNoteStatus = 'draft' | 'sent' | 'awaiting_approval' | 'approved' | 'rejected' | 'cancelled' | 'reversed';
export type AmountCheckState = null | 'mismatch' | 'use_vendor_requested' | 'use_vendor_approved' | 'kept';
/** Where a goods return's goods are (VR-3). null on money only, and before Sent. */
export type GoodsState = null | 'held' | 'released' | 'returned' | 'write_off_requested' | 'written_off' | 'restored';

export const GOODS_STATE_LABELS: Record<Exclude<GoodsState, null>, string> = {
  held: 'Goods held',
  released: 'Goods released',
  returned: 'Goods returned to vendor',
  write_off_requested: 'Write-off requested',
  written_off: 'Written off',
  restored: 'Goods back in stock',
};

export const DEBIT_NOTE_STATUS_LABELS: Record<DebitNoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent to vendor',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  reversed: 'Reversed',
};

export interface DebitNoteLine {
  lineNumber: number;
  description?: string;
  sku?: string;
  unit?: string;
  quantity: number | null;
  unitPrice?: number | null;
  taxRate?: number | null;
  /** The invoice gives the line no VAT rate; `taxRate` is the Settings VAT rate (`getVatRate`) charged instead (D44 #27). */
  taxRateAssumed?: boolean;
  amount: number;
  vatAmount?: number;
  total?: number;
  /** Goods: the batch the goods leave from. */
  layerId?: string | null;
  /** Weighed boxes that go back (their weight is `quantity`). */
  caseSeqs?: number[];
  /** What the hold placed when sent: batch units, and the billed-unit figure. */
  heldLevel1?: number | null;
  heldLevel2?: number | null;
}

export interface DebitNoteHistoryEntry {
  action: string;
  fromStatus: string | null;
  toStatus: string | null;
  note?: string;
  byName?: string;
  at: string;
}

export interface DebitNote {
  _id: string;
  debitNoteNumber: string;
  vendorInvoiceId: string;
  vendorInvoiceNumber?: string;
  vendorId?: string | null;
  vendorName?: string;
  currency: string;
  kind: DebitNoteKind;
  reasonKey: string;
  reasonNote?: string;
  lines: DebitNoteLine[];
  subtotal: number;
  vatTotal: number;
  total: number;
  status: DebitNoteStatus;
  sentAt?: string | null;
  sentVia?: 'email' | 'manual' | null;
  sentTo?: string;
  acceptedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  rejectionNote?: string;
  cancelledAt?: string | null;
  reversedAt?: string | null;
  reversalNote?: string;
  creditAmount?: number | null;
  application?: {
    accountsPayableId?: string | null;
    billRaised?: boolean;
    amount?: number;
    toBill?: number;
    toCreditBalance?: number;
    vendorCreditId?: string | null;
    appliedAt?: string | null;
  };
  vendorCreditNote?: {
    number?: string;
    date?: string | null;
    amount?: number | null;
    fileName?: string;
    mimeType?: string;
    attachedAt?: string | null;
  };
  amountCheck?: {
    state: AmountCheckState;
    vendorAmount?: number | null;
    ourAmount?: number | null;
    note?: string;
    requestedAt?: string | null;
    resolvedAt?: string | null;
  };
  goods?: {
    state: GoodsState;
    heldAt?: string | null;
    releasedAt?: string | null;
    writeOffNote?: string;
    writeOffRequestedAt?: string | null;
    writeOffDecidedAt?: string | null;
    movementIds?: string[];
  };
  history?: DebitNoteHistoryEntry[];
  /** VR-4: the client credit note this return was raised from ("Return to supplier?"), if any. */
  clientCreditNoteId?: string | null;
  clientCreditNoteNumber?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** One weighed box on a batch (catch weight). */
export interface ReturnableBox {
  seq: number;
  code: string;
  weightLevel1: number;
  weightRemainingLevel1: number;
  status: 'available' | 'reserved' | 'held';
  /** Held by the return being edited. */
  heldByThis: boolean;
  /** May be ticked for this return. */
  available: boolean;
  /** The box's weight in the billed unit. */
  quantity: number | null;
}

/**
 * The batch (StockLayer) an invoice line put into United stock, in the billed
 * unit (VR-3). A goods return takes only `freeQuantity`: what is left, less
 * what orders reserved and other returns hold.
 */
export interface ReturnableBatch {
  layerId: string;
  freeQuantity: number | null;
  remainingQuantity: number | null;
  reservedQuantity: number | null;
  heldElsewhereQuantity: number | null;
  heldHereQuantity: number | null;
  heldElsewhereOn: string[];
  expiryDate: string | null;
  /** Weighed boxes: the return ticks boxes instead of typing a quantity. */
  boxes: ReturnableBox[] | null;
  /** false when the receipt never said how many it received. */
  returnableByQuantity: boolean;
}

/** One vendor invoice line with what it billed and what is left to return. */
export interface ReturnableLine {
  lineNumber: number;
  description: string;
  sku: string;
  unit: string;
  wholeUnitsOnly: boolean;
  quantity: number | null;
  unitPrice: number | null;
  base: number | null;
  taxRate: number | null;
  /** The invoice gives the line no VAT rate; `taxRate` is the Settings VAT rate (`getVatRate`) a return charges instead (D44 #27). */
  taxRateAssumed: boolean;
  returnedQuantity: number;
  returnedAmount: number;
  returnedOn: string[];
  remainingQuantity: number | null;
  remainingAmount: number | null;
  /** null when the line put nothing into United stock: money only. */
  batch: ReturnableBatch | null;
}

export interface ReturnableInvoice {
  vendorInvoice: {
    _id: string;
    invoiceNumber: string;
    invoiceDate?: string | null;
    vendorName: string;
    currency: string | null;
  };
  lines: ReturnableLine[];
}

export interface DebitNoteDraft {
  vendorInvoiceId: string;
  kind: DebitNoteKind;
  reasonKey: string;
  reasonNote: string;
  /**
   * Goods: `quantity` (and optionally an edited `amount`), or `caseSeqs` on a
   * batch of weighed boxes. Money only: `amount`.
   */
  lines: Array<{ lineNumber: number; quantity?: number | null; amount?: number | null; caseSeqs?: number[] }>;
}

export interface DebitNotePreview {
  currency: string | null;
  kind: DebitNoteKind;
  lines: DebitNoteLine[];
  subtotal: number;
  vatTotal: number;
  total: number;
}

type Envelope<T> = { success: boolean; data: T };

/** Which return may be edited: a draft only. */
export function isDebitNoteEditable(status: DebitNoteStatus | undefined): boolean {
  return !status || status === 'draft';
}

/** What the payable is credited: the vendor's amount once an Admin approved it, else our total. */
export function creditedAmountOf(note: Pick<DebitNote, 'creditAmount' | 'total'>): number {
  return typeof note.creditAmount === 'number' ? note.creditAmount : note.total;
}

export async function listDebitNotes(params: { page?: number; limit?: number; status?: string; q?: string; vendorInvoiceId?: string } = {}) {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 25));
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  if (params.vendorInvoiceId) qs.set('vendorInvoiceId', params.vendorInvoiceId);
  return get<{ success: boolean; data: DebitNote[]; meta: { page: number; limit: number; total: number } }>(
    `/debit-notes?${qs.toString()}`
  );
}

export async function getDebitNote(id: string): Promise<DebitNote> {
  return (await get<Envelope<DebitNote>>(`/debit-notes/${id}`)).data;
}

export async function getNextDebitNoteNumber(): Promise<string> {
  return (await get<Envelope<{ debitNoteNumber: string }>>('/debit-notes/next-number')).data.debitNoteNumber;
}

export async function getReturnableInvoice(vendorInvoiceId: string, excludeId?: string): Promise<ReturnableInvoice> {
  const qs = excludeId ? `?excludeId=${encodeURIComponent(excludeId)}` : '';
  return (await get<Envelope<ReturnableInvoice>>(`/debit-notes/returnable/${vendorInvoiceId}${qs}`)).data;
}

export async function previewDebitNote(draft: DebitNoteDraft & { id?: string }): Promise<DebitNotePreview> {
  return (await postJson<Envelope<DebitNotePreview>>('/debit-notes/preview', draft)).data;
}

export async function createDebitNote(draft: DebitNoteDraft): Promise<DebitNote> {
  return (await postJson<Envelope<DebitNote>>('/debit-notes', draft)).data;
}

export async function updateDebitNote(id: string, draft: Partial<DebitNoteDraft>): Promise<DebitNote> {
  return (await put<Envelope<DebitNote>>(`/debit-notes/${id}`, draft)).data;
}

async function move(id: string, action: string, body: Record<string, unknown> = {}): Promise<DebitNote> {
  return (await postJson<Envelope<DebitNote>>(`/debit-notes/${id}/${action}`, body)).data;
}

export const markDebitNoteSent = (id: string, sentTo = '') => move(id, 'mark-sent', { sentTo });

/** What "Email to vendor" opens with (VR-6): the registered vendor's email on file, if any. */
export interface DebitNoteEmailDraft {
  to: string;
  vendorMatched: boolean;
  subject: string;
  body: string;
  attachmentName: string;
  /** Which state of the note this text was drafted from; sent back with the email (D180). */
  draftVersion: string;
}

export async function getDebitNoteEmailDraft(id: string): Promise<DebitNoteEmailDraft> {
  return (await get<Envelope<DebitNoteEmailDraft>>(`/debit-notes/${id}/email`)).data;
}

/** What "Email to vendor" sends: exactly what the dialog showed (D180). */
export interface DebitNoteEmail {
  /** May hold several addresses, separated by commas. */
  recipients: string;
  subject: string;
  body: string;
  draftVersion: string;
}

/**
 * "Email to vendor" (D44 #14): emails the PDF, and only when the email went does
 * the return become Sent.
 */
export const emailDebitNoteToVendor = (id: string, email: DebitNoteEmail) => move(id, 'email', { ...email });

/**
 * The vendor's copy as a PDF (D44 #17), in any status. Fetched with the bearer
 * token, because a plain link would arrive unauthenticated.
 */
export async function downloadDebitNotePdf(id: string, debitNoteNumber: string): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE_URL}/debit-notes/${id}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('The PDF could not be downloaded.');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${debitNoteNumber || 'debit-note'}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
export const markVendorAccepted = (id: string) => move(id, 'vendor-accepted');
export const markVendorRefused = (id: string, note = '') => move(id, 'vendor-refused', { note });
export const cancelDebitNote = (id: string, note = '') => move(id, 'cancel', { note });
/** Admin role only. */
export const approveDebitNote = (id: string) => move(id, 'approve');
/** Admin role only. */
export const sendBackDebitNote = (id: string, note = '') => move(id, 'send-back', { note });
/** Admin role only. */
export const reverseDebitNote = (id: string, note = '') => move(id, 'reverse', { note });

/** After "Vendor refused": the held goods go back to free stock. */
export const releaseDebitNoteGoods = (id: string, note = '') => move(id, 'release-goods', { note });
/** After "Vendor refused": ask an Admin to write the held goods off. The note is required (D44 #32). */
export const requestDebitNoteWriteOff = (id: string, note: string) => move(id, 'write-off', { note });
/** Admin role only. Final. */
export const approveDebitNoteWriteOff = (id: string) => move(id, 'write-off/approve');
/** Admin role only. The goods stay held. */
export const declineDebitNoteWriteOff = (id: string, note = '') => move(id, 'write-off/decline', { note });

export async function attachVendorCreditNote(
  id: string,
  body: { number: string; date: string; amount: number | string; file?: File | null }
): Promise<DebitNote> {
  const form = new FormData();
  form.append('number', body.number);
  form.append('date', body.date);
  form.append('amount', String(body.amount));
  if (body.file) form.append('file', body.file);
  return (await postFormData<Envelope<DebitNote>>(`/debit-notes/${id}/vendor-credit-note`, form)).data;
}

export async function getVendorCreditNoteUrl(id: string): Promise<string> {
  return (await get<Envelope<{ url: string }>>(`/debit-notes/${id}/vendor-credit-note/url`)).data.url;
}

export const keepOurAmount = (id: string, note: string) => move(id, 'amount-check/keep-ours', { note });
export const requestVendorAmount = (id: string, note = '') => move(id, 'amount-check/use-vendor', { note });
/** Admin role only. */
export const approveVendorAmount = (id: string) => move(id, 'amount-check/use-vendor/approve');
/** Admin role only. */
export const declineVendorAmount = (id: string, note = '') => move(id, 'amount-check/use-vendor/decline', { note });
